import { inject, observer } from "mobx-react";
import { Component } from "react";
import { type IntlShape, injectIntl } from "react-intl";
import { compose } from "nvent-web/utils/compose";

import { LoadingSection } from "nvent-web/components/LoadingSection/LoadingSection";
import { ProjectsStore } from "nvent-web/stores/Projects";
import { RoomsStore } from "nvent-web/stores/Rooms";
import { type BuildingType } from "nvent-web/types/BuildingType";
import { type Room } from "nvent-web/types/Room";
import { type SelectionGuideFormValues } from "nvent-web/types/SelectionGuideFormValues";

import { getAncestryString } from "../../utils/getAncestryString";
import StepHeading from "../StepHeading/StepHeading";

import RoomDetailsStepForm from "./components/RoomDetailsStepForm/RoomDetailsStepForm";

import { type WithRouterProps, withRouter } from "~/nvent-web/utils/withRouter";

interface RoomDetailsStepParams {
  projectId: string;
  roomId: string;
}

interface RoomDetailsStepInnerProps
  extends WithRouterProps, RoomDetailsStepParams {
  intl: IntlShape;
  rooms: RoomsStore;
  projects: ProjectsStore;
}

class RoomDetailsStepInner extends Component<RoomDetailsStepInnerProps> {
  private get projectId(): number {
    return Number(this.props.params.projectId);
  }

  private get roomId(): number {
    return Number(this.props.params.roomId);
  }

  private getBuildingType(): BuildingType | null {
    return (
      (this.props.projects.projectDetails &&
        this.props.projects.projectDetails.buildingType) ||
      null
    );
  }

  componentDidMount() {
    this.props.rooms.getRoom(this.projectId, this.roomId);
    this.props.projects.getProject(this.projectId);
  }

  render() {
    const { roomDetails, areRoomDetailsLoading, isRoomCreating } =
      this.props.rooms;
    const { projectDetails } = this.props.projects;

    const ancestryString = getAncestryString({
      projectName: projectDetails?.name,
      levels: roomDetails?.levels,
      roomName: roomDetails?.name,
    });

    if (areRoomDetailsLoading) {
      return <LoadingSection />;
    }

    return (
      <>
        <StepHeading step={2} ancestry={ancestryString} />
        {roomDetails && this.renderForm(roomDetails, isRoomCreating)}
      </>
    );
  }

  private initialValues(room: Room): SelectionGuideFormValues {
    return {
      floorConstruction: room.floorConstruction || null,
      floorFinish: room.floorFinish || null,
      area: room.area,
      installableArea: room.installableArea,
      quickHeating: room.quickHeating || false,
      areaType: room.areaType,
    };
  }

  private renderForm(values: Room, isRoomCreating: boolean) {
    return (
      <RoomDetailsStepForm
        isSubmitting={isRoomCreating}
        onCancel={this.handleCancel}
        initialValues={this.initialValues(values)}
        handleSubmit={this.handleSubmit}
        buildingType={this.getBuildingType()}
      />
    );
  }

  private handleSubmit = async (formValues: SelectionGuideFormValues) => {
    const { rooms, navigate } = this.props;

    await rooms.updateRoom(formValues, this.projectId, this.roomId);
    navigate(
      `/projects/${this.projectId}/rooms/${this.roomId}/selection-guide/recommendations`
    );
  };

  private handleCancel = () => {
    this.props.navigate(`/projects/${this.projectId}/rooms/${this.roomId}`);
  };
}

const RoomDetailsStep = compose(
  inject("rooms", "projects"),
  injectIntl,
  withRouter,
  observer
)(RoomDetailsStepInner);

export default RoomDetailsStep;
